import { Link, Outlet, useLocation } from '@tanstack/react-router'
import { useAuth } from '@/lib/auth'
import { cn } from '@/lib/utils'
import { APP_VERSION, GIT_SHA } from '@/lib/version'
import { ArhLogo } from '@/components/codex/ArhBrand'
import { LayoutDashboard, Boxes, Globe, Briefcase, Compass, Settings, LogOut, Sparkles, ScrollText, Zap, TrendingUp, Archive } from 'lucide-react'
import type { ReactNode } from 'react'

const navItems = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/apps', label: 'Portfolio', icon: Boxes },
  { to: '/hosting', label: 'Hosting Sites', icon: Globe },
  { to: '/pipeline', label: 'Acquisition', icon: Briefcase },
  { to: '/operations', label: 'Operations', icon: Zap },
  { to: '/deferred-work', label: 'Deferred Work', icon: Archive },
  { to: '/value', label: 'Value Engineering', icon: TrendingUp },
  { to: '/strategy', label: 'Strategy', icon: Compass },
  { to: '/changelog', label: 'Changelog', icon: ScrollText },
  { to: '/settings', label: 'Settings', icon: Settings },
] as const

export function Layout({ children }: { children?: ReactNode }) {
  const { user, signOutNow } = useAuth()
  const loc = useLocation()

  return (
    <div className="min-h-screen bg-navy-deep film-grain">
      <div className="flex">
        <Sidebar pathname={loc.pathname} userEmail={user?.email ?? ''} onSignOut={signOutNow} />
        <main className="flex-1 min-w-0">
          <TopBar />
          <div className="px-8 py-8 max-w-[1600px] mx-auto">
            {children ?? <Outlet />}
          </div>
        </main>
      </div>
    </div>
  )
}

function Sidebar({ pathname, userEmail, onSignOut }: { pathname: string; userEmail: string; onSignOut: () => void }) {
  return (
    <aside className="w-64 shrink-0 min-h-screen border-r border-bronze/15 bg-navy/30 backdrop-blur-md flex flex-col">
      <div className="px-6 py-8 border-b border-bronze/15">
        <Link to="/" className="block group">
          <ArhLogo framed className="w-44 max-w-full mb-5 px-3 py-2 transition-transform group-hover:-translate-y-0.5" />
          <div className="pl-1">
            <div className="eyebrow mb-1">Acquisition Console</div>
            <div className="font-display italic text-2xl text-cream leading-none">
              Portfolio<br /><span className="text-gold-soft">Preparation</span>
            </div>
          </div>
        </Link>
      </div>

      <nav className="flex-1 px-3 py-6 space-y-1">
        {navItems.map(({ to, label, icon: Icon }) => {
          const active = pathname === to || (to !== '/' && pathname.startsWith(to))
          return (
            <Link
              key={to}
              to={to}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-md',
                'font-body text-sm transition-all duration-150',
                active
                  ? 'bg-gold/12 text-gold-soft border border-gold/30'
                  : 'text-cream/65 hover:text-cream hover:bg-cream/5 border border-transparent',
              )}
            >
              <Icon size={16} />
              <span>{label}</span>
              {active && <Sparkles size={12} className="ml-auto text-gold-soft" />}
            </Link>
          )
        })}
      </nav>

      <div className="px-6 py-5 border-t border-bronze/15">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-cream/40 mb-2">Operator</div>
        <div className="font-body text-xs text-cream truncate mb-3">{userEmail}</div>
        <button
          onClick={onSignOut}
          className="flex items-center gap-2 text-xs text-cream/55 hover:text-danger transition-colors"
        >
          <LogOut size={12} />
          Sign out
        </button>
      </div>
    </aside>
  )
}

function TopBar() {
  const ts = new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
  return (
    <div className="border-b border-bronze/15 bg-navy/40 backdrop-blur-sm">
      <div className="px-8 py-3 flex items-center justify-between max-w-[1600px] mx-auto">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/45">
          {ts} · Texas Series LLC · Sole-Member-Managed
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/changelog"
            className="font-mono text-[10px] uppercase tracking-[0.18em] text-bronze hover:text-gold-soft transition-colors"
            title={`build ${GIT_SHA}`}
          >
            v{APP_VERSION}
          </Link>
          <span className="font-mono text-[10px] text-cream/30">·</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cream/40" title="Build SHA">
            {GIT_SHA}
          </span>
          <span className="size-1.5 rounded-full bg-success animate-pulse" />
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cream/55">Live</span>
        </div>
      </div>
    </div>
  )
}
